import { computed, effect, inject, Injectable, Signal } from "@angular/core";
import { UserStoreService } from "../store/user/user.store.service";
import { WizardView } from "../../models/view/wizard/wizard.view";
import { WizardType } from "../store/user/wizards.store.collection";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlossCreditsTransactionSyncService } from "../syncing/gloss-credits/gloss-credits-transaction-sync.service";

export interface Task {
  id: number;
  title: string;
  reward: number;
  rewardType: string;
  hasRoute: boolean;
}

export const tasks = [
  {
    id: 2,
    title: "explorePlugin",
    reward: 300,
    rewardType: "plugins",
    hasRoute: true,
  },
  {
    id: 3,
    title: "viewYourCredits",
    reward: 100,
    rewardType: "rewards",
    hasRoute: true,
  },
  {
    id: 4,
    title: "watchAdsToEarn",
    reward: 300,
    rewardType: "rewards",
    hasRoute: true,
  },
  {
    id: 5,
    title: "addManualAccount",
    reward: 200,
    rewardType: "accounts",
    hasRoute: true,
  },
  {
    id: 6,
    title: "fetchAnAccount",
    reward: 500,
    rewardType: "accounts",
    hasRoute: true,
  },
];

@Injectable({
  providedIn: "root",
})
export class GuideWizardService {
  protected userStoreService: UserStoreService = inject(UserStoreService);
  protected notificationService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private creditsService: CreditsService = inject(CreditsService);
  private log: LogService = inject(LogService);
  private glossCreditsTransactionSync: GlossCreditsTransactionSyncService = inject(
    GlossCreditsTransactionSyncService,
  );
  private appState: AppStateStoreService = inject(AppStateStoreService);
  private dialogFactory: DialogFactory;

  private wizardQueue: Task[] = [];

  constructor() {
    this.dialogFactory = new DialogFactory();
    effect(async () => {
      if (this.wizardTasks() && this.wizardTasks().length > 0) {
        while (this.wizardQueue.length > 0) {
          const task = this.wizardQueue.shift();
          await this.completeTask(task);
        }
      }
    });
  }

  wizardTasks: Signal<WizardView[]> = computed(() => {
    const wizards: WizardView[] = [];
    const wizard = this.userStoreService.wizards.wizardViews();
    if (Array.isArray(wizard)) {
      const currentWizards: WizardView[] = this.userStoreService.wizards.findWizardsByType(
        WizardType.guide,
      );
      if (currentWizards.length > 0) {
        currentWizards.forEach((currentWizard) => {
          wizards.push(currentWizard.clone());
        });
      }
    }
    return wizards;
  });

  async wizardInitialise() {
    const wizardTasks = this.wizardTasks();
    if (wizardTasks && wizardTasks.findIndex((w) => w.type == WizardType.guide) < 0) {
      const defaultGuideWizards = this.userStoreService.wizards.getDefaultGuideWizard();
      await this.userStoreService.wizards.saveToVault(defaultGuideWizards, true);
    }
  }

  async editGuideWizardByTask(task: Task, isComplete: boolean) {
    const targetTaskWizard = this.getWizardByTask(task.id);
    if (!targetTaskWizard) {
      this.notificationService.notify(NotificationEnum.error, "errorOccurred", "taskNotFound");
      throw new Error(`No wizard found for task ${task.id}`);
    }
    targetTaskWizard.step = { isComplete: isComplete, stepIndex: task.id };
    await this.userStoreService.wizards.save(targetTaskWizard);
  }

  /**
   * Returns whether the task ended up complete (or already was). Never throws: callers include
   * the constructor's queue-drain effect and redirect handlers (manage-accounts.component.ts,
   * task-info-tooltips.component.ts) with no error handling on this call, so a rejection here
   * would silently break everything downstream of it (e.g. an rxjs pipeline erroring out for the
   * rest of the component's lifetime). Failures are surfaced via notification instead.
   */
  async completeTask(task: Task): Promise<boolean> {
    if (!(this.wizardTasks() && this.wizardTasks().length > 0)) {
      this.wizardQueue.push(task);
      return false;
    }
    // Resolve the wizard before depositing, and guard on it directly, so a task id with no
    // matching wizard row can't slip past `?.` as "not complete" - fire a deposit and then have
    // editGuideWizardByTask's own not-found check silently no-op the persist.
    const wizard = this.getWizardByTask(task.id);
    if (!wizard) {
      this.notificationService.notify(NotificationEnum.error, "errorOccurred", "taskNotFound");
      return false;
    }
    if (!wizard.step.isComplete) {
      try {
        this.appState.startProcess("general", "Credit reward in progress");
        const applied = await this.creditsService.createTransactionOnTaskComplete(task.reward);
        if (applied) {
          // Deposit carries no idempotency key (same hazard documented on the referral drain's
          // cross-tab lock in gloss-notification-handler.service.ts): a vault-write failure here,
          // after a successful deposit, leaves isComplete unpersisted and the task retryable, so a
          // retry re-deposits. Retrying the persist narrows that window but doesn't close it fully
          // - only a server-side idempotency key on the deposit itself would do that.
          try {
            await this.persistTaskCompletionWithRetry(task);
          } catch (error) {
            // Retries exhausted. The deposit already landed, so this is a bookkeeping failure,
            // not a "no reward" one - surface it rather than throwing out of completeTask.
            this.log.error(`Guide task persist failed after retries: ${String(error)}`);
            this.notificationService.notify(
              NotificationEnum.error,
              "errorOccurred",
              "unexpectedError",
            );
            return false;
          }
          await this.glossCreditsTransactionSync.syncGlossCreditsTransactions();
        } else {
          // No deposit was made. createTransactionOnTaskComplete() itself polls
          // (ensureCreditSystemReady, 6 x 500ms) before giving up, so this is a settled outcome,
          // not a transient one - either there's genuinely no credits account for this user, or
          // the credits API is unreachable. Either way don't block guide progression on an
          // optional plugin: persist directly, since no deposit was made there's nothing to
          // guard against re-firing.
          this.notificationService.notify(
            NotificationEnum.error,
            "errorOccurred",
            "creditNotAwarded",
          );
          try {
            await this.editGuideWizardByTask(task, true);
          } catch (error) {
            this.log.error(`Guide task persist failed: ${String(error)}`);
            return false;
          }
        }
      } finally {
        this.appState.endProcess("general");
      }
    } else {
      this.notificationService.notify(NotificationEnum.info, "guideInfo", "taskAlreadyCompleted");
    }
    if (task.id === 6) {
      this.dialogFactory.openGuideDialog(true);
    } else {
      this.dialogFactory.openGuideDialog(
        this.userStoreService.subscription.activeSubscription()?.isPremium,
      );
    }
    return true;
  }

  /**
   * Retry the post-deposit persist a few times before giving up. Reduces - but, absent a
   * server-side idempotency key on the deposit, cannot eliminate - the window where a transient
   * vault-write failure leaves a paid-out task retryable (see the comment at the call site).
   */
  private async persistTaskCompletionWithRetry(
    task: Task,
    attempts = 3,
    delayMs = 500,
  ): Promise<void> {
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        await this.editGuideWizardByTask(task, true);
        return;
      } catch (error) {
        if (attempt === attempts) {
          throw error;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }

  getWizardByTask(taskId: number) {
    if (this.wizardTasks() && this.wizardTasks().length > 0) {
      return this.wizardTasks()?.find(
        (wizardTask: WizardView) => wizardTask.step.stepIndex === taskId,
      );
    }
    return undefined;
  }

  getWizardByGuideTitle(guideTitle: string) {
    return this.getWizardByTask(this.getTaskByGuideTitle(guideTitle)?.id);
  }

  getTaskByGuideTitle(guideTitle: string) {
    return tasks.find((task) => task.title === guideTitle);
  }
}
